import React, { useEffect, useMemo, useState } from 'react'
import {
    Badge,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    useTheme,
} from '@material-ui/core'
import FilterListIcon from '@material-ui/icons/FilterList'
import CloseIcon from '@material-ui/icons/Close'
import CheckIcon from '@material-ui/icons/Check'
import ClearAllIcon from '@material-ui/icons/ClearAll'
import { Container } from '../../components'
import {
    ALL_PRIORITIES,
    ALL_VENDOR_TASK_STATUSES,
    FilterCriteria,
    PRIORITY_COLORS,
    PRIORITY_LABELS,
    Priority,
    VENDOR_TASK_STATUS_COLORS,
    VENDOR_TASK_STATUS_LABELS,
    VendorProjectSortField,
    VendorTaskStatus,
} from '../../models'
import { typographyStyle } from '../../theme/typographyStyle'

interface Props {
    criteria: FilterCriteria
    onChange: (next: FilterCriteria) => void
    onReset: () => void
    active: boolean
}

const SORT_OPTIONS: { value: VendorProjectSortField; label: string }[] = [
    { value: 'title', label: 'Title' },
    { value: 'priority', label: 'Priority' },
    { value: 'start_date', label: 'Start Date' },
    { value: 'end_date', label: 'End Date' },
    { value: 'created_at', label: 'Created' },
    { value: 'updated_at', label: 'Last Updated' },
    { value: 'assigned_by_name', label: 'Assigned By' },
]

const SectionLabel = ({ children }: { children: React.ReactNode }) => {
    const theme = useTheme()
    return (
        <Container
            style={{
                ...typographyStyle(theme.typography.caption),
                color: theme.palette.text.secondary,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                marginBottom: theme.spacing(1),
            }}
        >
            {children}
        </Container>
    )
}

export const FilterButton = (props: Props) => {
    const { criteria, onChange, onReset, active } = props
    const theme = useTheme()

    const [open, setOpen] = useState(false)
    const [draft, setDraft] = useState<FilterCriteria>(criteria)

    useEffect(() => {
        if (open) setDraft(criteria)
    }, [open, criteria])

    const countCriteria = (c: FilterCriteria): number => {
        let n = 0
        if (c.dateFrom || c.dateTo) n += 1
        if (c.priority && c.priority.length > 0) n += 1
        if (c.status && c.status.length > 0) n += 1
        if (c.sortBy) n += 1
        return n
    }

    const activeCount = useMemo(() => countCriteria(criteria), [criteria])
    const draftCount = useMemo(() => countCriteria(draft), [draft])

    const togglePriority = (p: Priority) => {
        const cur = new Set(draft.priority ?? [])
        if (cur.has(p)) cur.delete(p)
        else cur.add(p)
        setDraft({
            ...draft,
            priority: cur.size === 0 ? null : Array.from(cur),
        })
    }

    const toggleStatus = (s: VendorTaskStatus) => {
        const cur = new Set(draft.status ?? [])
        if (cur.has(s)) cur.delete(s)
        else cur.add(s)
        setDraft({
            ...draft,
            status: cur.size === 0 ? null : Array.from(cur),
        })
    }

    const handleApply = () => {
        onChange(draft)
        setOpen(false)
    }

    const handleClear = () => {
        onReset()
        setOpen(false)
    }

    return (
        <>
            <Badge
                badgeContent={activeCount}
                color="secondary"
                overlap="circle"
                invisible={activeCount === 0}
            >
                <Button
                    variant={active ? 'contained' : 'outlined'}
                    startIcon={<FilterListIcon />}
                    onClick={() => setOpen(true)}
                    style={{
                        backgroundColor: active ? 'white' : 'transparent',
                        color: active ? theme.palette.brand1.main : 'white',
                        borderColor: 'rgba(255,255,255,0.6)',
                        fontWeight: 700,
                    }}
                >
                    Filter
                </Button>
            </Badge>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle disableTypography style={{ paddingBottom: 0 }}>
                    <Container
                        alignItems="center"
                        style={{ gap: theme.spacing(1) }}
                    >
                        <Container
                            alignItems="center"
                            justifyContent="center"
                            style={{
                                width: 36,
                                height: 36,
                                borderRadius: 8,
                                backgroundColor: '#e8efef',
                                color: theme.palette.brand1.main,
                            }}
                        >
                            <FilterListIcon />
                        </Container>
                        <Container direction="column" flex={1}>
                            <Container
                                style={{
                                    ...typographyStyle(theme.typography.h6),
                                    fontWeight: 700,
                                }}
                            >
                                Filter Projects
                            </Container>
                            <Container
                                style={{
                                    ...typographyStyle(
                                        theme.typography.caption,
                                    ),
                                    color: theme.palette.text.secondary,
                                }}
                            >
                                Narrow down what you see and choose a sort order
                            </Container>
                        </Container>
                        <IconButton size="small" onClick={() => setOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Container>
                </DialogTitle>
                <Divider style={{ margin: theme.spacing(2, 3, 0) }} />
                <DialogContent>
                    <Container
                        direction="column"
                        style={{ gap: theme.spacing(2.5) }}
                    >
                        <Container direction="column">
                            <SectionLabel>Date Range</SectionLabel>
                            <Container style={{ gap: theme.spacing(2) }}>
                                <TextField
                                    label="From"
                                    type="date"
                                    value={draft.dateFrom ?? ''}
                                    onChange={(e) =>
                                        setDraft({
                                            ...draft,
                                            dateFrom: e.target.value || null,
                                        })
                                    }
                                    InputLabelProps={{ shrink: true }}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                />
                                <TextField
                                    label="To"
                                    type="date"
                                    value={draft.dateTo ?? ''}
                                    onChange={(e) =>
                                        setDraft({
                                            ...draft,
                                            dateTo: e.target.value || null,
                                        })
                                    }
                                    InputLabelProps={{ shrink: true }}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                />
                            </Container>
                        </Container>

                        <Container direction="column">
                            <SectionLabel>Priority</SectionLabel>
                            <Container
                                style={{
                                    gap: theme.spacing(1),
                                    flexWrap: 'wrap',
                                }}
                            >
                                {ALL_PRIORITIES.map((p) => {
                                    const selected = (
                                        draft.priority ?? []
                                    ).includes(p)
                                    const c = PRIORITY_COLORS[p]
                                    return (
                                        <Button
                                            key={p}
                                            size="small"
                                            variant={
                                                selected
                                                    ? 'contained'
                                                    : 'outlined'
                                            }
                                            onClick={() => togglePriority(p)}
                                            style={{
                                                backgroundColor: selected
                                                    ? c.fg
                                                    : undefined,
                                                color: selected
                                                    ? 'white'
                                                    : c.fg,
                                                borderColor: c.fg,
                                                fontWeight: 700,
                                                textTransform: 'none',
                                            }}
                                        >
                                            {PRIORITY_LABELS[p]}
                                        </Button>
                                    )
                                })}
                            </Container>
                        </Container>

                        <Container direction="column">
                            <SectionLabel>Has Task With Status</SectionLabel>
                            <Container
                                style={{
                                    gap: theme.spacing(1),
                                    flexWrap: 'wrap',
                                }}
                            >
                                {ALL_VENDOR_TASK_STATUSES.map((s) => {
                                    const selected = (
                                        draft.status ?? []
                                    ).includes(s)
                                    const colors = VENDOR_TASK_STATUS_COLORS[s]
                                    return (
                                        <Button
                                            key={s}
                                            size="small"
                                            variant={
                                                selected
                                                    ? 'contained'
                                                    : 'outlined'
                                            }
                                            onClick={() => toggleStatus(s)}
                                            style={{
                                                backgroundColor: selected
                                                    ? colors.fg
                                                    : undefined,
                                                color: selected
                                                    ? 'white'
                                                    : colors.fg,
                                                borderColor: colors.fg,
                                                fontWeight: 700,
                                                textTransform: 'none',
                                            }}
                                        >
                                            {VENDOR_TASK_STATUS_LABELS[s]}
                                        </Button>
                                    )
                                })}
                            </Container>
                        </Container>

                        <Container direction="column">
                            <SectionLabel>Sort By</SectionLabel>
                            <Container style={{ gap: theme.spacing(2) }}>
                                <FormControl
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                >
                                    <InputLabel id="vp-sort-by">
                                        Field
                                    </InputLabel>
                                    <Select
                                        labelId="vp-sort-by"
                                        label="Field"
                                        value={draft.sortBy ?? ''}
                                        onChange={(e) => {
                                            const v = e.target.value as
                                                | VendorProjectSortField
                                                | ''
                                            setDraft({
                                                ...draft,
                                                sortBy: v ? v : null,
                                                sortOrder:
                                                    v && !draft.sortOrder
                                                        ? 'asc'
                                                        : draft.sortOrder,
                                            })
                                        }}
                                    >
                                        <MenuItem value="">
                                            Smart default
                                        </MenuItem>
                                        {SORT_OPTIONS.map((opt) => (
                                            <MenuItem
                                                key={opt.value}
                                                value={opt.value}
                                            >
                                                {opt.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <FormControl
                                    fullWidth
                                    disabled={!draft.sortBy}
                                    variant="outlined"
                                    size="small"
                                >
                                    <InputLabel id="vp-sort-order">
                                        Order
                                    </InputLabel>
                                    <Select
                                        labelId="vp-sort-order"
                                        label="Order"
                                        value={draft.sortOrder ?? 'asc'}
                                        onChange={(e) =>
                                            setDraft({
                                                ...draft,
                                                sortOrder: e.target.value as
                                                    | 'asc'
                                                    | 'desc',
                                            })
                                        }
                                    >
                                        <MenuItem value="asc">
                                            Ascending
                                        </MenuItem>
                                        <MenuItem value="desc">
                                            Descending
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                            </Container>
                        </Container>
                    </Container>
                </DialogContent>
                <DialogActions
                    style={{
                        padding: theme.spacing(1.5, 3),
                        backgroundColor: '#fafbfb',
                        borderTop: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Button
                        onClick={handleClear}
                        startIcon={<ClearAllIcon />}
                        disabled={draftCount === 0 && activeCount === 0}
                        style={{
                            color: theme.palette.text.secondary,
                            fontWeight: 600,
                            textTransform: 'none',
                        }}
                    >
                        Clear all
                    </Button>
                    <Container flex={1} />
                    <Button
                        variant="outlined"
                        onClick={() => setOpen(false)}
                        style={{
                            fontWeight: 600,
                            textTransform: 'none',
                            marginRight: theme.spacing(1),
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleApply}
                        disableElevation
                        startIcon={<CheckIcon />}
                        style={{
                            fontWeight: 700,
                            textTransform: 'none',
                            minWidth: 140,
                            boxShadow:
                                '0 1px 2px rgba(70,176,131,0.2), 0 4px 12px rgba(70,176,131,0.15)',
                        }}
                    >
                        Apply
                        {draftCount > 0 ? ` · ${draftCount}` : ''}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
